import type { UserRole } from "../types.js";

/**
 * Validação do formulário "Cadastrar usuário" (Design.md §7). Lógica PURA (sem
 * React) para ser testável em node. Espelha o `createUserSchema` do backend
 * (apps/api/src/users.ts) — a microcopy de erro é literal/verbatim para casar
 * com a validação do servidor (pt-BR, §10).
 */
export interface UserFormValues {
  name: string;
  email: string;
  role: UserRole;
}

export interface UserFormErrors {
  name?: string;
  email?: string;
}

// Mesmo formato do backend (apps/api/src/users.ts:34).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EMPTY_USER_FORM: UserFormValues = {
  name: "",
  email: "",
  role: "OPERATOR"
};

/** Erros de validação client-side (mesma microcopy do backend). */
export function validateUserForm(values: UserFormValues): UserFormErrors {
  const errors: UserFormErrors = {};
  if (values.name.trim().length === 0) {
    errors.name = "Informe o nome completo.";
  }
  if (!EMAIL_RE.test(values.email.trim().toLowerCase())) {
    errors.email = "Informe um e-mail válido.";
  }
  return errors;
}

export function hasUserFormErrors(errors: UserFormErrors): boolean {
  return errors.name !== undefined || errors.email !== undefined;
}

/** Payload normalizado para POST /users (e-mail em minúsculas, igual ao backend). */
export function toCreateUserInput(values: UserFormValues): UserFormValues {
  return {
    name: values.name.trim(),
    email: values.email.trim().toLowerCase(),
    role: values.role
  };
}

/**
 * Mapeia um erro da API para erros de campo + mensagem geral. O backend retorna
 * 409 USER_ALREADY_EXISTS para e-mail duplicado e pode devolver erros de campo
 * Zod em `fields`. Pura: recebe primitivos, não acopla `ApiFetchError`.
 */
export function mapUserApiError(error: {
  code: string;
  message: string;
  fields: Record<string, string> | null;
}): { fieldErrors: UserFormErrors; generalMessage: string | null } {
  if (error.code === "USER_ALREADY_EXISTS") {
    return {
      fieldErrors: { email: "Este e-mail já está cadastrado." },
      generalMessage: null
    };
  }
  const fieldErrors: UserFormErrors = {};
  if (error.fields !== null) {
    if (typeof error.fields.name === "string") {
      fieldErrors.name = error.fields.name;
    }
    if (typeof error.fields.email === "string") {
      fieldErrors.email = error.fields.email;
    }
  }
  return {
    fieldErrors,
    generalMessage: hasUserFormErrors(fieldErrors) ? null : error.message
  };
}

/** Iniciais para o avatar (1–2 letras, maiúsculas). */
export function userInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Rótulo pt-BR do papel (Design.md §7). */
export function roleLabel(role: UserRole): string {
  return role === "ADMIN" ? "Admin" : "Operador";
}
